import React, { useEffect, useState } from "react";
import { DownloadSimple, X, Share, Plus } from "@phosphor-icons/react";

const DISMISS_KEY = "quilla_install_dismissed";
const IOS_HINT_KEY = "quilla_ios_hint_shown";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPad moderno reporta Mac + touch:
  const iPadOS = /Macintosh/.test(ua) && "ontouchend" in document;
  return iOSDevice || iPadOS;
}

/**
 * InstallPrompt — banner discreto para instalar QUILLA como PWA.
 * · Android/Chrome/Edge: usa el evento beforeinstallprompt.
 * · iOS Safari: no dispara ese evento, muestra instrucciones "Compartir > Agregar…".
 * · Nunca aparece si ya está instalada o si el usuario la descartó.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // ya instalada
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS: no hay evento. Mostramos la pista UNA sola vez, con pequeño delay.
    if (isIOS() && localStorage.getItem(IOS_HINT_KEY) !== "1") {
      const t = setTimeout(() => setIosHint(true), 3000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBip);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    setVisible(false);
    setDeferred(null);
    if (choice.outcome === "dismissed") {
      try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
    }
  };

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
  };

  const dismissIos = () => {
    setIosHint(false);
    try { localStorage.setItem(IOS_HINT_KEY, "1"); } catch {}
  };

  if (iosHint) {
    return (
      <div className="fixed bottom-3 left-3 right-3 md:left-auto md:right-6 md:max-w-sm z-[60]"
           data-testid="ios-install-hint">
        <div className="rounded-xl bg-white shadow-2xl border border-[color:var(--q-border)] p-4">
          <div className="flex items-start gap-3">
            <img src="/quilla-icon-192.png" alt="" className="w-10 h-10 rounded-lg shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[color:var(--q-navy)]">Instala QUILLA en tu iPhone</div>
              <div className="text-xs text-[color:var(--q-muted)] mt-1 leading-relaxed">
                Toca <Share size={12} weight="bold" className="inline align-[-2px]"/> Compartir y luego{" "}
                <span className="whitespace-nowrap">
                  <Plus size={12} weight="bold" className="inline align-[-2px]"/> "Agregar a pantalla de inicio"
                </span>.
              </div>
            </div>
            <button onClick={dismissIos} data-testid="ios-hint-close"
                    className="text-[color:var(--q-muted)] hover:text-[color:var(--q-navy)] shrink-0">
              <X size={16}/>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!visible) return null;

  return (
    <div className="fixed bottom-3 left-3 right-3 md:left-auto md:right-6 md:max-w-sm z-[60]"
         data-testid="install-prompt">
      <div className="rounded-xl bg-white shadow-2xl border border-[color:var(--q-border)] p-4">
        <div className="flex items-start gap-3">
          <img src="/quilla-icon-192.png" alt="" className="w-10 h-10 rounded-lg shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[color:var(--q-navy)]">
              Instala QUILLA en tu pantalla de inicio
            </div>
            <div className="text-xs text-[color:var(--q-muted)] mt-0.5">
              Acceso rápido desde el celular · abre a pantalla completa.
            </div>
          </div>
          <button onClick={dismiss} data-testid="install-dismiss"
                  className="text-[color:var(--q-muted)] hover:text-[color:var(--q-navy)] shrink-0">
            <X size={16}/>
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={dismiss} className="q-btn-ghost text-xs h-9 px-3">Ahora no</button>
          <button onClick={install} data-testid="install-accept"
                  className="q-btn-primary text-xs h-9 px-4 flex-1 flex items-center justify-center gap-1.5">
            <DownloadSimple size={14} weight="bold"/> Instalar
          </button>
        </div>
      </div>
    </div>
  );
}
